import { useEffect, useState, type CSSProperties } from 'react';
import { AvatarStage } from './components/AvatarStage';
import { Composer } from './components/Composer';
import { ControlsRow } from './components/ControlsRow';
import { EnglishPanel } from './components/EnglishPanel';
import { GlossStrip } from './components/GlossStrip';
import { Header, type AppView } from './components/Header';
import { ListeningBar } from './components/ListeningBar';
import { PracticeView } from './components/PracticeView';
import { Settings } from './components/Settings';
import { Thread } from './components/Thread';
import { stepMs } from './lib/constants';
import {
  activeMessage,
  activeTokens,
  currentIndex,
  currentToken,
  glossSizePx,
  playLabel,
  showsNotes,
  stageChip,
  stageChipLive,
  stageCounter,
  stageSign,
  stageSignLength,
} from './state/reducer';
import { usePractice } from './state/usePractice';
import { useSignpost } from './state/useSignpost';

export function App() {
  const signpost = useSignpost();
  const { state } = signpost;
  const [view, setView] = useState<AppView>('chat');
  // The drill lives at App level so its streak survives a trip back to chat,
  // but it only runs its clock while it is the visible view.
  const practice = usePractice({ active: view === 'practice' });

  // One stage at a time: entering the drill pauses the chat reply.
  const { togglePlay } = signpost;
  useEffect(() => {
    if (view === 'practice' && state.playing) togglePlay();
  }, [view, state.playing, togglePlay]);

  const tokens = activeTokens(state);
  const index = currentIndex(state);

  const frame = {
    '--gloss-scale': state.glossScale,
    '--gloss-size': `${glossSizePx(state)}px`,
  } as CSSProperties;

  return (
    <div className="app" style={frame}>
      <div className="pad-top" />

      <Header
        modeLabel={state.mode === 'Deaf-first' ? 'Deaf-first' : 'Learner mode'}
        view={view}
        onSetView={setView}
        onOpenSettings={signpost.openSettings}
      />
      <div className="rule-ink" />

      {view === 'practice' ? (
        <PracticeView practice={practice} />
      ) : (
        <>
          <AvatarStage
            sign={stageSign(state)}
            signLength={stageSignLength(state)}
            nmf={currentToken(state).nmf ?? ''}
            chip={stageChip(state)}
            chipLive={stageChipLive(state)}
            index={index}
            total={tokens.length}
            counter={stageCounter(state)}
            dialect={state.dialect}
            playing={state.playing}
            busy={state.busy}
            cycleMs={stepMs(state.speed)}
          />

          <GlossStrip tokens={tokens} current={index} onSeek={signpost.seek} />
          <div className="rule-ink" />

          <ControlsRow
            playLabel={playLabel(state)}
            speed={state.speed}
            englishOpen={state.englishOpen}
            onTogglePlay={signpost.togglePlay}
            onSetSpeed={signpost.setSpeed}
            onToggleEnglish={signpost.toggleEnglishPanel}
          />
          <div className="rule-divider" />

          {state.englishOpen && (
            <>
              <EnglishPanel english={activeMessage(state)?.english ?? ''} />
              <div className="rule-divider" />
            </>
          )}

          <Thread
            messages={state.msgs}
            activeId={state.activeId}
            openEnglish={state.openEnglish}
            showNotes={showsNotes(state)}
            busy={state.busy}
            error={state.err}
            onReplay={signpost.replay}
            onToggleEnglish={signpost.toggleMessageEnglish}
          />

          {state.listening && <ListeningBar simulated={state.simulated} partial={state.partial} />}

          <Composer
            draft={state.draft}
            listening={state.listening}
            onDraftChange={signpost.setDraft}
            onSend={signpost.send}
            onToggleMic={signpost.toggleMic}
          />
        </>
      )}
      <div className="pad-bottom" />

      {state.settingsOpen && (
        <Settings
          mode={state.mode}
          englishOpen={state.englishOpen}
          dialect={state.dialect}
          glossScale={state.glossScale}
          onSetMode={signpost.setMode}
          onSetEnglishOpen={signpost.setEnglishOpen}
          onSetDialect={signpost.setDialect}
          onSetGlossScale={signpost.setGlossScale}
          onClose={signpost.closeSettings}
        />
      )}
    </div>
  );
}
